<template>
  <ContentTemplate title="Profile" :container-size-small="true">
    <div class="row">
      <div class="col-12">
        <div class="member-details">
          <div class="row row-user-avatar">
            <div class="col-12 user-header">
              <AvatarWidget
                  :image-key="selectedMember.avatar"
                  :social-avatar="selectedMember.socialAvatar"
                  image-size="imageL"
                  :full-name-avatar="selectedMember.fullNameAvatar"
                  :alt="selectedMember.fullNameAvatar"
                  :title="selectedMember.fullName"
                  bigtext="bigtext">
              </AvatarWidget>
              <div class="user-content">
                <h1 class="user-name mb-2">{{ selectedMember.fullName }}</h1>
                <div class="buttons vertical-middle">
                    <span class="me-1">
                      <TagElement :class="getRoleTagClass">
                         {{ getFormattedRole }}
                      </TagElement>
                    </span>
                </div>
              </div>
            </div>
          </div>

          <FormGroupSection
              :form-element="fullNameInput"
              v-model:input-value="form[fullNameInput.input.name]"
          />
          <FormGroupSection
              :form-element="primaryEmailInput"
              v-model:input-value="form[primaryEmailInput.input.name]"
          />
          <FormGroupSection
              :form-element="positionInput"
              v-model:input-value="form[positionInput.input.name]"
          />
        </div>
      </div>
    </div>

    <div class="row mt-5">
      <div class="col-12 mb-4">
        <h2>Access</h2>
      </div>
      <div class="col-12">
        <FormGroupSection
            :form-element="statusInput"
            v-model:input-value="form[statusInput.input.name]"
            @change="changeMemberStatus($event)"
        />
        <FormGroupSection v-if="selectedMember.workspaceRole !== 'OWNER'"
                          :form-element="invitedInput"
                          v-model:input-value="form[invitedInput.input.name]"
        />
        <FormGroupSection
            :form-element="lastActivityInput"
            v-model:input-value="form[lastActivityInput.input.name]"
        />
      </div>
    </div>

    <div class="row mt-5" v-if="selectedMember.workspaceRole !== 'OWNER'">
      <div class="col-12 mb-3">
        <h2>Roles</h2>
      </div>
      <div class="form-group">
        <div class="row">
          <div class="col-4">
            <FormGroupLabelSection
                label="Roles"
                icon="Users"
                icon-type="fal"
            />
          </div>
          <div class="col-8">
            <SelectableDropdownElement
                v-model:value="form['roles']"
                @change="changeMemberRole($event)"
                title="Roles"
                type="buttonselect"
                name="roles"
                placeholder="Roles"
                :options="rolesOptions"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="row" style="margin-top:15px;" v-if="selectedMember.workspaceRole !== 'OWNER'">
      <div class="form-group">
        <div class="row">
          <div class="col-4">
            <FormGroupLabelSection
                label="Assigned"
                icon="User"
                icon-type="fal"
            />
          </div>
          <div class="col-8">
            <SelectableDropdownElement
                type="buttonselect"
                title="Add a project"
                name="Add a project"
                :options="projectOptions"
                static-label-mode
                @change="assignProject($event)"
                :is-disable="form['roles'] !== 'MEMBER'"
            />

            <div v-for="project in allUserProjects" :key="project.id" class="assigned-project">
              <div class="col-6">
                <span class="name">{{ project.name }}</span>
              </div>

              <SelectableDropdownElement
                  :value="project.projectRole"
                  @change="changeProjectRole(project.id, $event)"
                  title="Project Roles"
                  type="buttonselect"
                  name="pRoles"
                  placeholder="Project Roles"
                  :options="memberOptions"
                  dropdowncss="dropdown-menu-large"
              />

              <ActionDropdownElement
                  title="Actions"
                  :options="leftProjectActions"
                  type="button"
                  icon="EllipsisVertical"
                  class="actions-icon"
                  @change="leftProject(project.id)"
              />
            </div>
          </div>

        </div>
      </div>
    </div>
    <div class="row mt-5 mb-5" v-if="selectedMember.workspaceRole !== 'OWNER'">
      <div class="col-12 mb-3">
        <h2>Workspace</h2>
      </div>
      <div class="col-12">
        <ButtonElement class="btn--ms btn--danger"
                       @clickOnButton="removeFromWorkspace()">
          Remove Account from Workspace
        </ButtonElement>
      </div>
    </div>
  </ContentTemplate>
</template>
<script lang="ts">
import {defineComponent} from 'vue'
import {
  ActionDropdownElement,
  ButtonElement,
  ContentTemplate,
  FormGroupLabelSection,
  FormGroupSection,
  SelectableDropdownElement,
  TagElement,
} from '@shared/components'

import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'

dayjs.extend(localizedFormat)
import useVuelidate from '@vuelidate/core'
import {required} from '@vuelidate/validators'
import AvatarWidget from '@shared/components/widgets/profile/avatar.widget.vue'
import {useWsMemberStore} from "@domains/settings/stores/workspace-member.store";
import {mapStores} from "pinia";
import {useWorkspaceStore} from "@shared/stores/workspace.store";
import {useGeneralStore} from "@shared/stores/general.store";
import {CommonNs, type DropdownNs, type PopupNs, ProjectNs, SettingNs, WorkspaceNs} from "@shared/types";
import {useProjectStore} from "@domains/dashboard/stores/project.store";
import {toast} from '@shared/ms-components'

export default defineComponent({
  name: 'MemberDetailsView',
  components: {
    AvatarWidget,
    ContentTemplate,
    FormGroupSection,
    ButtonElement,
    TagElement,
    FormGroupLabelSection,
    SelectableDropdownElement,
    ActionDropdownElement
  },
  data() {
    const stOptions = [
      {
        iconType: 'fal',
        icon: 'UserCheck',
        name: 'Active',
        value: 'ACTIVE'
      },
      {
        iconType: 'fal',
        icon: 'UserX',
        name: 'Disabled',
        value: 'INACTIVE'
      }
    ] as DropdownNs.DropdownOptionsItem[]

    const memOptions = [
      {
        iconType: 'fal',
        icon: 'ShieldCheck',
        name: 'Viewer',
        value: 'VIEWER',
        content: 'Can just view the project.'
      },
      {
        iconType: 'fal',
        icon: 'UserCog',
        name: 'Editor',
        value: 'EDITOR',
        content: 'Can view and edit project.'
      },
      {
        iconType: 'fal',
        icon: 'Users',
        name: 'Manager',
        value: 'MANAGER',
        content: 'Can view, edit and add members to the project.'
      }
    ] as DropdownNs.DropdownOptionsItem[]

    const rlOptions = [
      {
        iconType: 'fal',
        icon: 'UserCog',
        name: 'Admin',
        value: 'ADMIN',
        content: 'Can manage workspace, control user access, and update billing details.'
      },
      {
        iconType: 'fal',
        icon: 'Users',
        name: 'Member',
        value: 'MEMBER',
        content: 'Can access the projects as manager, editor or viewer depending on permission.'
      }
    ] as DropdownNs.DropdownOptionsItem[]

    const lProjectActions = [
      {
        iconType: 'fal',
        icon: 'Trash2',
        name: 'Remove'
      }
    ] as DropdownNs.DropdownOptionsItem[]

    return {
      selectedMember: {} as WorkspaceNs.Member,
      allWorkspaceProjects: [] as ProjectNs.Project[],
      allUserProjects: [] as ProjectNs.Project[],

      form: {} as { [key: string]: string },

      fullNameInput: {} as CommonNs.InputFormGroup,
      primaryEmailInput: {} as CommonNs.InputFormGroup,
      positionInput: {} as CommonNs.InputFormGroup,
      statusInput: {} as CommonNs.InputFormGroup,
      invitedInput: {} as CommonNs.InputFormGroup,
      lastActivityInput: {} as CommonNs.InputFormGroup,
      rolesInput: {} as CommonNs.InputFormGroup,
      assignedProjectInput: {} as CommonNs.InputFormGroup,

      statusOptions: stOptions,
      rolesOptions: rlOptions,
      memberOptions: memOptions,
      leftProjectActions: lProjectActions,
      projectOptions: [] as DropdownNs.DropdownOptionsItem[]
    }
  },
  computed: {
    ...mapStores(useWorkspaceStore, useWsMemberStore, useGeneralStore, useProjectStore),
    getFormattedRole(): string {
      const role = this.selectedMember.workspaceRole
      return role.charAt(0) + role.slice(1).toLowerCase()
    },
    getRoleTagClass(): string {
      const role = this.selectedMember.workspaceRole
      switch (role) {
        case 'OWNER':
          return 'tag--orange'
        case 'ADMIN':
          return 'tag--blue'
        case 'MEMBER':
          return 'tag--neutral'
        default:
          return 'tag--neutral'
      }
    }
  },
  setup() {
    return {v$: useVuelidate()}
  },
  validations() {
    return {
      form: {
        fullName: {required},
        primaryEmail: {required},
        position: {}
      }
    }
  },
  beforeMount() {
    this.selectedMember = this.wsMemberStore.getSelectedMember
    if (!this.selectedMember.userName) {
      // Go to member list
      this.goToMembersPage()
    }

    this.buildForm()
    this.initFormData()
  },
  methods: {
    formatDate(value: string): string {
      if (value) {
        return dayjs(value).format('LL')
      }
      return ''
    },
    buildForm(): void {
      this.fullNameInput = {
        label: 'Full Name',
        icon: 'User',
        iconType: 'fal',
        input: {
          name: 'fullName',
          type: 'text',
          placeholder: 'Full Name',
          value: '',
          isDisable: true
        }
      } as CommonNs.InputFormGroup

      this.primaryEmailInput = {
        label: 'Primary Email',
        icon: 'Mail',
        iconType: 'fal',
        input: {
          name: 'primaryEmail',
          type: 'text',
          placeholder: 'md@flexwork.io',
          value: {},
          isDisable: true,
          isLocked: true
        }
      } as CommonNs.InputFormGroup

      this.positionInput = {
        label: 'Position',
        icon: 'Briefcase',
        iconType: 'fal',
        input: {
          name: 'position',
          type: 'text',
          placeholder: 'Position',
          value: '',
          isDisable: true
        }
      } as CommonNs.InputFormGroup

      this.statusInput = {
        label: 'Status',
        iconType: 'fal',
        icon: 'LockOpen',
        input: {
          name: 'status',
          type: 'select',
          placeholder: 'Status',
          selectOptions: this.statusOptions,
          value: '',
          isDisable: this.selectedMember.workspaceRole === 'OWNER'
        }
      } as CommonNs.InputFormGroup

      this.invitedInput = {
        label: 'Invited',
        iconType: 'fal',
        icon: 'Calendar',
        input: {
          name: 'invited',
          type: 'text',
          isDisable: true,
          value: ''
        }
      } as CommonNs.InputFormGroup

      this.lastActivityInput = {
        label: 'Last Activity',
        iconType: 'fal',
        icon: 'LogOut',
        input: {
          name: 'lastActivity',
          type: 'text',
          isDisable: true,
          value: ''
        }
      } as CommonNs.InputFormGroup

      this.rolesInput = {
        label: 'Roles',
        iconType: 'fal',
        icon: 'Users',
        input: {
          name: 'roles',
          type: 'select',
          placeholder: 'Roles',
          selectOptions: this.rolesOptions,
          isDisable: false,
          value: ''
        }
      } as CommonNs.InputFormGroup

      this.assignedProjectInput = {
        label: 'Assigned',
        iconType: 'fal',
        icon: 'User',
        input: {
          name: 'assignedProject',
          type: 'add-dropdown',
          placeholder: 'Add a project',
          isDisable: false
        }
      } as CommonNs.InputFormGroup
    },
    initFormData() {
      this.form = {
        fullName: this.selectedMember.fullName,
        primaryEmail: this.selectedMember.userName,
        position: '',
        status: this.selectedMember.memberStatus,
        invited: this.formatDate(this.selectedMember.invitedDate),
        lastActivity: this.formatDate(this.selectedMember.lastActivity),
        roles: this.selectedMember.workspaceRole,
        assignedProject: '',
        removeWorkspace: ''
      }

      if (this.selectedMember.workspaceRole === 'MEMBER') this.loadProjectInfos()
    },
    initProjectOptions() {
      this.projectOptions = this.allWorkspaceProjects.filter((x) => !this.allUserProjects.some((y) => (x.id === y.id)))
          .map((p) => ({
                iconType: 'fal',
                icon: 'Plus',
                name: p.name,
                value: p.id.toString()
              } as DropdownNs.DropdownOptionsItem
          ))
    },
    changeMemberRole(selectedRole: DropdownNs.DropdownOptionsItem) {
      const payload = {
        workspaceId: this.workspaceStore.getSelectedWorkspaceId,
        memberId: this.selectedMember.id,
        newRole: selectedRole.value
      } as WorkspaceNs.ChangeWorkspaceMemberRoleRequest

      this.wsMemberStore.changeWorkspaceMemberRole(payload).then(() => {
        if (selectedRole.value === 'MEMBER') {
          this.loadProjectInfos()
        }
        toast.success('Member role has been updated')
      })
    },
    changeMemberStatus(selectedStatus: DropdownNs.DropdownOptionsItem) {
      const payload = {
        workspaceId: this.workspaceStore.getSelectedWorkspaceId,
        memberId: this.selectedMember.id,
        newStatus: selectedStatus.value
      } as WorkspaceNs.ChangeWorkspaceMemberStatusRequest

      this.wsMemberStore.changeWorkspaceMemberStatus(payload).then(() => {
        toast.success('Member status has been updated')
        this.wsMemberStore.loadWorkspaceMembers(this.workspaceStore.getSelectedWorkspaceId).then(() => {
        })
      })
    },
    loadProjectInfos() {
      const workspaceId = this.workspaceStore.getSelectedWorkspaceId;

      this.projectStore.loadAllProjectsInWorkspace({workspaceId}).then((data) => {
        this.allWorkspaceProjects = data

        this.projectStore.loadUserProjectsInWorkspace({
          workspaceId,
          userName: this.selectedMember.userName
        } as ProjectNs.GetUserProjectsInWorkspaceRequest).then((response) => {
          this.allUserProjects = response
          this.initProjectOptions()
        })
      })
    },
    changeProjectRole(projectId: string, selectedRole: DropdownNs.DropdownOptionsItem) {
      const payload = {
        workspaceId: this.workspaceStore.getSelectedWorkspaceId,
        projectId,
        userName: this.selectedMember.userName,
        projectRole: selectedRole.value
      } as ProjectNs.ChangeProjectMemberStatusRequest

      this.wsMemberStore.changeProjectMemberRole(payload).then(() => {
        toast.success('Project role has been updated')
      })
    },
    assignProject(selectedProjectItem: DropdownNs.DropdownOptionsItem) {
      const index = this.allWorkspaceProjects.findIndex((x) => (x.id.toString() === selectedProjectItem.value))
      const selectedProject = this.allWorkspaceProjects[index]
      const payload = {
        workspaceId: this.workspaceStore.getSelectedWorkspaceId,
        projectId: selectedProject.id,
        userName: this.selectedMember.userName,
        projectRole: 'VIEWER'
      } as ProjectNs.ChangeProjectMemberStatusRequest

      this.wsMemberStore.addMemberToProject(payload).then(() => {
        selectedProject.projectRole = 'VIEWER'
        this.allUserProjects = [...this.allUserProjects, selectedProject]
        this.allWorkspaceProjects.splice(index, 1)
        this.initProjectOptions()
        toast.success('Member has been assigned to the project')
      })
    },

    leftProject(projectId: string) {
      const index = this.allUserProjects.findIndex((x) => (x.id.toString() === projectId.toString()))
      const selectedProject = this.allUserProjects[index]

      const payload = {
        workspaceId: this.workspaceStore.getSelectedWorkspaceId,
        projectId: selectedProject.id,
        userName: this.selectedMember.userName
      } as ProjectNs.ChangeProjectMemberStatusRequest

      this.wsMemberStore.deleteMemberFromProject(payload).then(() => {
        this.allWorkspaceProjects = [...this.allWorkspaceProjects, selectedProject]
        this.allUserProjects.splice(index, 1)
        this.initProjectOptions()
        toast.success('Member has been removed from the project')
      })
    },

    redirectToChangePrimaryMail() {
      this.$router.push({name: SettingNs.SettingRouteEnums.SUGGESTEMAIL})
    },
    removeFromWorkspace() {
      const messagePayload = {
        content: {
          title: 'Delete',
          message: 'Removing this member from the workspace will also remove all associated project memberships.'
        } as PopupNs.PopupContent,
        callback: {
          content: 'Remove',
          function: this.redirectRemoveFromWorkspace
        } as PopupNs.PopupCallback
      } as PopupNs.Popup

      this.generalStore.showPopup(messagePayload)
    },
    redirectRemoveFromWorkspace() {
      const workspaceId = this.workspaceStore.getSelectedWorkspaceId;
      const payload = {
        workspaceId,
        memberId: this.selectedMember.id,
        removeInvitation: false
      } as WorkspaceNs.RemoveWorkspaceMemberRequest

      this.wsMemberStore.deleteMemberFromWorkspace(payload).then(() => {
        this.wsMemberStore.loadWorkspaceMembers(workspaceId).then(() => this.goToMembersPage())
      })
    },
    goToMembersPage() {
      this.$router.push({name: SettingNs.SettingRouteEnums.MEMBERS})
    }
  }
})
</script>

<style lang="scss" scoped>
.member-details {
  width: 100%;
  height: 100%;
  padding: 30px 30px 20px;
  background-color: var(--card-bg);
  border: 1px solid var(--card-border);
  box-shadow: var(--card-shadow);
  border-radius: $br-sm;
}

.user-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;

  .user-content {
    margin-left: 15px;
  }
}

.user-button {
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
}

.user-avatar {

  width: 80px;
  height: 80px;
  font-size: var(--fs-24);

  &.text-avatar {

    h1 {

      font-size: var(--fs-24);
    }
  }
}

.assigned-project {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--card-bg);
  box-shadow: var(--card-shadow);
  border-radius: $br-sm;
  font-size: var(--fs-14);
  padding: var(--space-16);
  margin: var(--space-16) var(--space-0);
  gap: var(--space-16);

  &:hover {
    box-shadow: var(--card-shadow-active);
  }

  .name {
    font-weight: 500;
  }
}
</style>
